import React from 'react';
import { getContext, getEasing, getDuration, isAnimationDisabled } from 'common';
import { useSpring, animated } from 'react-spring';
import _merge from 'lodash/merge';
import AppToolbar, { AppToolbarProps } from 'components/AppToolbar';
import TOKENS from '@nokia-csf-uxr/freeform-design-tokens';
import Styled from './styles';
import UtilityDrawerHideButton, { UtilityDrawerHideButtonProps } from './UtilityDrawerHideButton';

const DEFAULT_APP_TOOLBAR_STYLE = ({ RTL, onHideClick }) => {
  const style : React.CSSProperties = { minHeight: 0, height: '100%', width: '100%' };
  if (onHideClick) {
    if (RTL) {
      style.paddingLeft = '3rem';
    } else {
      style.paddingRight = '3rem';
    }
  }
  return style;
};

const ANIMATED_DIV_STYLE = {
  width: '100%',
  height: '100%',
  display: 'flex'
};

export const UtilityDrawerContext = React.createContext<any>({});

export interface UtilityDrawerProps extends React.ComponentPropsWithRef<'div'> {
  /** Place UtilityDrawer content here, eg. <UtilityDrawerSection /> */
  children: React.ReactNode;
  /** If false, the drawer will hide. */
  show?: boolean;
  /** Forward props to AppToolbar, see AppToolbar for more information on available props. */
  appToolbarProps?: Partial<AppToolbarProps>;
  /** Props for the Utility Drawer `AnimationContainer` wrapper element. */
  wrapperProps?: React.ComponentPropsWithRef<any>;
  /** Configure the animation for height of `AnimationContainer` wrapper element. Uses 'react-spring' see https://react-spring.io for more information. */
  wrapperAnimationProps?: any;
  /** Configure the animation for content opacity. Uses 'react-spring' see https://react-spring.io for more information. */
  contentAnimationProps?: any;
  /** If true, disable all motion animation for the icon button. This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** Props for the UtilityDrawerHideButton. */
  hiddenButtonProps?: Partial<UtilityDrawerHideButtonProps>;
  /** If set, the button which is used to show and hide the Utility Drawer will show up. */
  onHideClick?: (arg: React.MouseEvent | React.KeyboardEvent) => void;
  /**
   * @ignore
   * To override the style of the component.
   * */
  style?: React.CSSProperties;
}

/** Utility Drawer is a sub-type of drawer based on AppToolbar that gives quick access to detail information related to a content area.
 *  Any other props than the explicitly defined props will be provided to the 'UtilityDrawer' element. */
const UtilityDrawer = React.forwardRef<HTMLElement, UtilityDrawerProps>((props: UtilityDrawerProps, ref) =>
  getContext(({ RTL, disableAnimation }) => {
    const {
      children,
      show,
      style,
      onHideClick,
      hiddenButtonProps,
      wrapperProps,
      appToolbarProps,
      wrapperAnimationProps,
      contentAnimationProps,
      disableAnimation: propsdisableAnimation,
      ...otherProps
    } = props;
    const { style: passedStyle, ...otherAppToolbarProps } = appToolbarProps || {};
    const { iconButtonProps: hiddenIconButtonProps, ...otherHiddenButtonProps } = hiddenButtonProps;

    const { style: wrapperStyle, ...otherWrapperProps } = wrapperProps || {};
    const { height: wrapperHeight } = wrapperStyle || {};

    const resolvedHeight = wrapperHeight || '3.5rem';

    const resolvedAppToolbarStyle = _merge({}, DEFAULT_APP_TOOLBAR_STYLE({ RTL, onHideClick }), passedStyle);

    const handleHideClick = (e) => {
      onHideClick && onHideClick(e);
    };
    const heightAnimation = useSpring(wrapperAnimationProps || {
      height: !show ? '0.75rem' : resolvedHeight,
      config: {
        duration: getDuration(TOKENS.SPEED.SPEED_SLOW),
        easing: getEasing(TOKENS.TRANSITION.TRANSITION_ACCELERATE)
      },
      immediate: isAnimationDisabled(propsdisableAnimation, disableAnimation),
    });

    const opacityAnimation = useSpring(contentAnimationProps || {
      opacity: !show ? 0 : 1,
      delay: !show ? 0 : getDuration(TOKENS.SPEED.SPEED_FAST),
      config: {
        duration: getDuration(TOKENS.SPEED.SPEED_FAST),
        easing: getEasing(TOKENS.TRANSITION.TRANSITION_ACCELERATE)
      },
      immediate: isAnimationDisabled(propsdisableAnimation, disableAnimation),
    });
    return (
      // This wrapper is to ensure UtilityDrawer animation height from bottom
      <Styled.AnimationContainer height={resolvedHeight} style={wrapperStyle} {...otherWrapperProps}>
        <Styled.AnimationPlaceholder />
        <Styled.UtilityDrawer
          as={animated.div}
          height={resolvedHeight}
          style={{ ...heightAnimation, ...style }}
          ref={ref}
          $RTL={RTL}
          {...otherProps}
        >
          <AppToolbar
            elevationProps={{
              elevationIndex: 2,
              dark: true
            }}
            style={resolvedAppToolbarStyle}
            {...otherAppToolbarProps}
          >
            <UtilityDrawerContext.Provider value={{ show }}>
              <animated.div
                style={{
                  ...ANIMATED_DIV_STYLE,
                  ...opacityAnimation
                }}
              >
                {children}
              </animated.div>
            </UtilityDrawerContext.Provider>
          </AppToolbar>
          {onHideClick && (
            <UtilityDrawerHideButton
              show={show}
              iconButtonProps={{ onClick: handleHideClick, ...hiddenIconButtonProps }}
              {...otherHiddenButtonProps}
            />
          )}
        </Styled.UtilityDrawer>
      </Styled.AnimationContainer>
    );
  }));

UtilityDrawer.displayName = 'UtilityDrawer';

UtilityDrawer.defaultProps = {
  style: {},
  show: true,
  contentAnimationProps: undefined,
  wrapperAnimationProps: undefined,
  disableAnimation: undefined,
  appToolbarProps: {},
  wrapperProps: {},
  hiddenButtonProps: {},
  onHideClick: undefined
};

export { UtilityDrawer as default };
